import { useEffect, useState } from 'react';
import * as R from 'ramda';
import Sidebar from 'components/Dashboard/Sidebar';
import I18nHelper from 'helpers/i18n';
import { getPossessiveUserFirstName } from 'helpers/user';
import Services from 'components/Dashboard/Services';
import MainContent from 'components/MainContent';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useCurrentUser from 'hooks/useCurrentUser';
import { fetchUserMe } from 'actions/userMe';
import { useDispatch } from 'react-redux';
import { update } from 'store/slices/user';
import {
  useGetClassConnectEnrollments,
  useGetUserEnrollments
} from 'actions/enrollments';
import { toastError } from 'components/Toast';
import ProfessionalDevelopment from 'components/Dashboard/ProfessionalDevelopment';
import { Flex } from 'components/Flex';
import useSubscriptionTours from 'hooks/useSubscriptionTours';
import {
  Button,
  SpotlightPopover,
  SpotlightPopoverNavigation,
  Text
} from '@teachstonellc/component-library';

const i18n = new I18nHelper('fe.pages.dashboard');

const messages = {
  'insights-subscription-required': {
    title: 'Access Denied',
    description:
      'Hey! It looks like you don’t have access to our Instant Insights product.'
  },
  'generic-subscription-required': {
    title: 'Access Denied',
    description: 'Hey! It looks like you don’t have access to this product.'
  }
};

function Dashboard() {
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const searchError = search.get('error');

  const userName = getPossessiveUserFirstName(
    currentUser.first_name,
    currentUser.preferred_language
  );
  const { data: enrollments, isSuccess } = useGetUserEnrollments(
    currentUser.id
  );
  const {
    data: classConnectEnrollments,
    isSuccess: classConnectEnrollmentsSuccess
  } = useGetClassConnectEnrollments(currentUser.id);
  let serviceKeys = R.map(R.prop('key'), currentUser.services);
  const showLearningResources = serviceKeys.includes('PDLibrary');

  // See hooks/useSubscriptionTours.ts for dismiss semantics.
  const {
    tours,
    isLoading: isSubscriptionToursLoading,
    markSeen,
    markStepSeen
  } = useSubscriptionTours();

  const activeTours = !isSubscriptionToursLoading ? tours : [];
  const [tourStepIndex, setTourStepIndex] = useState(0);
  const activeTour = activeTours[tourStepIndex] ?? null;
  const isLastTourStep = tourStepIndex >= activeTours.length - 1;

  function handleTourClose() {
    markSeen();
    setTourStepIndex(0);
  }

  function handleTourNext() {
    if (isLastTourStep) {
      handleTourClose();
      return;
    }
    setTourStepIndex(index => index + 1);
  }

  function handleTourPrevious() {
    setTourStepIndex(index => Math.max(index - 1, 0));
  }

  function handleTourCtaClick() {
    if (!activeTour) {
      return;
    }
    // Other pending tours survive — see markStepSeen in useSubscriptionTours.ts.
    markStepSeen(activeTour.key);
    setTourStepIndex(0);
    if (activeTour.route) {
      // External URL — hard navigation, not React Router's navigate() (same
      // pattern as Services.tsx).
      window.location.href = activeTour.route;
    }
  }

  useEffect(() => {
    fetchUserMe().then(response => {
      dispatch(update(response.data));
    });
  }, [dispatch]);

  useEffect(() => {
    document.title = i18n.t('user_dashboard', '{{user}} Dashboard', {
      name: userName
    });
  }, [userName]);

  useEffect(() => {
    const message = messages[searchError as keyof typeof messages];

    if (message) {
      toastError(message);
    }
  }, [searchError]);

  if (currentUser.id && !currentUser.onboarding_viewed_at) {
    navigate('/users/onboarding', { replace: true });
  }

  return (
    <>
      <MainContent maxWidth={1024}>
        <div className="mt-9 mb-4 ml-[43px] min-[666px]:ml-[22rem]">
          <h1 className="welcome">
            {i18n.t('user_dashboard', '{{name}} Dashboard', {
              name: getPossessiveUserFirstName(
                currentUser.first_name,
                currentUser.preferred_language
              )
            })}
          </h1>
        </div>
        <div className="dashboard-wrapper">
          <div className="body-left">
            <Sidebar />
          </div>
          <div className="body-right">
            <div className="body-content">
              {currentUser.is_LR_off_with_teacher_role &&
              R.isEmpty(currentUser.services) ? (
                <div>
                  <div>
                    <p>
                      {i18n.t(
                        'services_not_availabe',
                        'You do not have any active services, feel free to check out these free resources available on our '
                      )}
                      <a href="https://teachstone.com/resources/">
                        {i18n.t('public_website', 'public website.')}
                      </a>
                    </p>
                  </div>
                  <div className="contact">
                    <p>
                      {i18n.t(
                        'contact_message',
                        'If you have questions please reach out to our Customer Support team.'
                      )}
                    </p>
                  </div>
                </div>
              ) : (
                <Flex direction="column">
                  {isSuccess && classConnectEnrollmentsSuccess ? (
                    <ProfessionalDevelopment
                      enrollments={enrollments.user_sis_enrollments}
                      learningResources={showLearningResources}
                      classConnectEnrollments={
                        classConnectEnrollments.user_sis_enrollments
                      }
                      recommendations={[]}
                    />
                  ) : null}
                  <Services />
                </Flex>
              )}
            </div>
          </div>
        </div>
      </MainContent>

      {activeTour && (
        <SpotlightPopover
          open
          targetSelector={activeTour.targetSelector}
          showNewBadge
          onClose={handleTourClose}
          content={
            <div className="space-y-3">
              {/* Backend copy; layout is placeholder pending Figma. */}
              <Text variant="h3">{activeTour.title}</Text>
              <Text variant="body1" className="text-ds-text-resting">
                {activeTour.description}
              </Text>
              <Button variant="primary" fullWidth onClick={handleTourCtaClick}>
                {activeTour.ctaLabel}
              </Button>
              {activeTours.length > 1 && (
                <SpotlightPopoverNavigation
                  steps="multi"
                  previousAction={
                    tourStepIndex > 0
                      ? { label: 'Previous', onClick: handleTourPrevious }
                      : undefined
                  }
                  nextAction={{
                    label: isLastTourStep ? 'Done' : 'Next',
                    onClick: handleTourNext
                  }}
                  counter={{
                    currentStep: tourStepIndex,
                    totalSteps: activeTours.length
                  }}
                />
              )}
            </div>
          }
        />
      )}
    </>
  );
}

export default Dashboard;
